import Store from './Store'
import { action, computed, flow, makeObservable, observable } from 'mobx'
import ClientService from '../services/ClientService'
import Client from '../models/Client'
import Paginator from '../models/Paginator'
import clone from '../utils/clone'
import Address from '../models/Address'
import ClientsAddress from '../models/ClientsAddress'
import Equipment from '../models/Equipment'
import Form from '../models/Form'
import ClientsAddressesEquipment from '../models/ClientsAddressesEquipment'

class ClientsStore extends Store {
  /**
   * @type {Client}
   */
  @observable client = new Client()
  backupClient = {}

  /**
   * @type {Address}
   */
  @observable clientInspectionAddress = new Address()

  /**
   *
   * @type {ClientsAddress}
   */
  @observable clientsAddress = new ClientsAddress()

  @observable clientsAddressesEquipment = new ClientsAddressesEquipment()
  /**
   * @type {array<Equipment>}
   */
  @observable equipments = []
  /**
   * @type {array<Form>}
   */
  @observable forms = []
  /**
   * @type {array<Client>}
   */
  @observable clients = []
  @observable paging = {}
  @observable _tmpQuery = {}

  @computed
  get isDirty () {
    return this.client.isDirty
  }

  constructor (loading = true) {
    super()
    makeObservable(this)
    this.isLoading = loading
    this._tmpQuery = sessionStorage.getItem('clientsStore')
      ? JSON.parse(sessionStorage.getItem('clientsStore'))
      : {}
    this.sendFilters()
  }

  @action
  newClient () {
    this.client = new Client()
    this.client.setOriginalData({})
    this.backupClient = { }
  }

  @flow
  * loadAll () {
    this.isLoading = true
    const result = yield ClientService.getClients(this.query)
    this.paging.Clients = new Paginator(result.paginator.Clients)
    this.clients = []
    for (const client of result.clients) {
      const clientObj = new Client()
      clientObj.setOriginalData(client)
      this.clients.push(clientObj)
    }
    this.isLoading = false
  }

  @flow
  * loadOne (clientId, force = false) {
    if (this.client.id !== clientId || force) {
      this.isLoading = true
      const result = yield ClientService.getClient(clientId)
      if (result) {
        this.backupClient = clone(result.client)
        this.client = new Client()
        this.client.setOriginalData(result.client)
      }

      this.isLoading = false
    }
  }

  @action
  createClientInspectionAddress () {
    this.clientInspectionAddress = new Address()
    this.clientInspectionAddress.setOriginalData({})
  }

  @flow
  * loadOneAddress (clientAddressId, force = false) {
    if (this.clientInspectionAddress.id !== clientAddressId || force) {
      this.isLoading = true
      const result = yield ClientService.getClientInspectionAddress(clientAddressId)
      if (result) {
        this.clientInspectionAddress = new Address()
        this.clientInspectionAddress.setOriginalData(result.clientsAddress.address)
      }
      this.isLoading = false
    }
  }

  @flow
  * loadOneInspectionAddressEquipment (clientsAddressEquipmentId, force = false) {
    if (this.clientsAddressesEquipment.id !== clientsAddressEquipmentId || force) {
      this.isLoading = true
      const result = yield ClientService.getInspectionsAddressesEquipment(clientsAddressEquipmentId)
      for (const equipment of result.equipments) {
        const equipmentObj = new Equipment()
        equipmentObj.setOriginalData(equipment)
        this.equipments.push(equipmentObj)
      }
      for (const form of result.forms) {
        this.forms.push(new Form(form))
      }
      this.clientsAddressesEquipment = new ClientsAddressesEquipment()
      this.clientsAddressesEquipment.setOriginalData(result.clientsAddressesEquipment)
      this.clientsAddress = this.clientsAddressesEquipment.clients_address
      this.isLoading = false
    }
  }

  @flow
  * loadOneInspectionAddressEquipmentDependencies (clientAddressId) {
    this.isLoading = true
    const result = yield ClientService.getInspectionsAddressesEquipmentDependencies(clientAddressId)
    this.clientsAddress = new ClientsAddress()
    this.clientsAddress.setOriginalData(result.clientsAddress)
    for (const equipment of result.equipments) {
      const equipmentObj = new Equipment()
      equipmentObj.setOriginalData(equipment)
      this.equipments.push(equipmentObj)
    }
    for (const form of result.forms) {
      this.forms.push(new Form(form))
    }
    this.clientsAddressesEquipment = new ClientsAddressesEquipment()
    this.clientsAddressesEquipment.setOriginalData({})
    for (const equipment of this.equipments) {
      if (this.clientsAddressesEquipment.equipment_id === equipment.id) {
        this.clientsAddressesEquipment.equipment = equipment
      }
    }
    this.isLoading = false
  }

  @action
  revert () {
    this.client = new Client()
    this.client.setOriginalData(this.backupClient)
  }

  @flow
  * save () {
    this.isSaving = true
    try {
      let response
      if (!this.client.id) {
        response = yield ClientService.createClient(this.client.toJson())
      } else {
        response = yield ClientService.saveClient(this.client.id, this.client.toJson())
      }
      this.isSaving = false
      if (response.result.message) {
        this.addToast(response.result.message, response.result.success ? 'success' : 'error')
      }
      this.client.setOriginalData(response.result.client)
      this.backupClient = response.result.client
      return response.result
    } catch (e) {
      this.addToast('Impossible de rejoindre le serveur en ce moment. Veuillez réessayer', 'error')
      throw e
    }
  }

  @flow
  * saveInspectionAddress (clientId = null, addressId = null) {
    this.isSaving = true
    try {
      let response
      if (addressId) {
        response = yield ClientService.editClientInspectionAddress(addressId, { address: this.clientInspectionAddress.toJson() })
      } else {
        response = yield ClientService.createClientInspectionAddress(clientId, { address: this.clientInspectionAddress.toJson() })
      }
      this.isSaving = false
      if (response.result.message) {
        this.addToast(response.result.message, response.result.success ? 'success' : 'error')
      }
      return response.result
    } catch (e) {
      this.addToast('Impossible de rejoindre le serveur en ce moment. Veuillez réessayer', 'error')
    }
  }

  @flow
  * saveInspectionAddressEquipment (clientAddressId = null, clientAddressEquipmentId = null) {
    this.isSaving = true
    try {
      let response
      const data = this.clientsAddressesEquipment.toJson()
      if (clientAddressEquipmentId) {
        response = yield ClientService.editInspectionsAddressesEquipment(clientAddressEquipmentId, data)
      } else {
        response = yield ClientService.createInspectionsAddressesEquipment(clientAddressId, data)
      }
      this.isSaving = false
      if (response.result.message) {
        this.addToast(response.result.message, response.result.success ? 'success' : 'error')
      }
      return response.result
    } catch (e) {
      this.addToast('Impossible de rejoindre le serveur en ce moment. Veuillez réessayer', 'error')
    }
  }

  @action
  changePage (newPage) {
    if (newPage <= this.paging.Clients.pageCount && newPage > 0) {
      this.paging.Clients.page = newPage
      this.query.page = newPage
    }
  }

  @action
  sendFilters () {
    for (const [key, value] of Object.entries(this._tmpQuery)) {
      this.query[key] = value
    }
    this.query.page = 1
    this.clients = []
    sessionStorage.setItem('clientsStore', JSON.stringify(this._tmpQuery))
  }

  @action
  clearFilters () {
    this._tmpQuery = {}
    this.query = {}
    this.clients = []
    sessionStorage.removeItem('clientsStore')
  }

  @flow
  * sendEmailLogin (clientId) {
    const response = yield ClientService.sendLoginEmail(clientId)
    if (!response.result.success) {
      this.errorMessage = response.result.message
    }
  }
}

export default ClientsStore
